import { m } from "framer-motion";
import { Shield, HardHat, FileCheck, Phone, Hammer } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useQuoteForm } from "@/components/QuoteFormContext";
import { siteConfig } from "@/lib/site-config";
import { clientImage, clientImageSrcSet, heroPhoto } from "@/lib/services-data";
import FormEmbed from "@/components/FormEmbed";

const badges = [
  { icon: Shield, label: "Licensed Texas LLC" },
  { icon: FileCheck, label: "Insurance Claim Experts" },
  { icon: HardHat, label: "Free Inspections" },
  { icon: Hammer, label: "All Trades In-House" },
];

const stats = [
  { value: "100", label: "Customer Satisfaction", suffix: "%" },
  { value: "10", label: "Services Offered", suffix: "" },
  { value: "10", label: "DFW Cities Served", suffix: "+" },
];

const HeroSection = () => {
  const { openQuoteForm } = useQuoteForm();

  return (
    <section
      id="home"
      className="relative min-h-[100svh] flex items-center overflow-hidden"
    >
      {/*
        The LCP element. index.html preloads the same srcset, so the fetch is
        already in flight by the time React renders this; the two must stay in
        step or the browser downloads the photo twice.
      */}
      <img
        src={clientImage(heroPhoto.file, heroPhoto.width, heroPhoto.ratio)}
        srcSet={clientImageSrcSet(heroPhoto.file, heroPhoto.ratio, [...heroPhoto.widths])}
        sizes="100vw"
        alt="Completed kitchen renovation with quartz island by R&R House Renovation LLC in the DFW metroplex"
        className="absolute inset-0 w-full h-full object-cover"
        loading="eager"
        fetchPriority="high"
        decoding="async"
        width={heroPhoto.width}
        height={heroPhoto.height}
      />
      <div className="absolute inset-0 hero-overlay" />
      <div className="absolute inset-0 bg-gradient-to-br from-[hsl(0,0%,4%/0.82)] via-[hsl(0,0%,6%/0.7)] to-[hsl(0,60%,16%/0.5)]" />
      <div className="absolute inset-0 gradient-mesh opacity-40" />

      <m.div
        className="absolute top-20 right-20 w-96 h-96 rounded-full opacity-25 hidden md:block"
        style={{ background: "radial-gradient(circle, hsl(var(--primary) / 0.35), transparent 70%)" }}
        animate={{ scale: [1, 1.1, 1], opacity: [0.18, 0.3, 0.18] }}
        transition={{ duration: 6, repeat: Infinity, ease: "easeInOut" }}
      />
      <m.div
        className="absolute bottom-40 left-10 w-64 h-64 rounded-full opacity-20 hidden md:block"
        style={{ background: "radial-gradient(circle, hsl(var(--secondary) / 0.4), transparent 70%)" }}
        animate={{ scale: [1, 1.15, 1], opacity: [0.12, 0.25, 0.12] }}
        transition={{ duration: 8, repeat: Infinity, ease: "easeInOut", delay: 2 }}
      />

      <div className="relative z-10 container-max px-4 pt-20 pb-32 md:pt-24 md:pb-32 w-full">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-start">
          <div className="lg:col-span-7">
            <m.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, ease: "easeOut" }}
            >
              <m.div
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.15, duration: 0.5 }}
                className="inline-flex items-center gap-2 bg-white/10 backdrop-blur-md border border-white/20 text-white text-xs sm:text-sm font-semibold px-4 sm:px-5 py-2 rounded-full mb-6 shadow-lg"
              >
                <HardHat className="h-4 w-4 text-secondary" />
                <span className="text-white">Aubrey, TX · Serving the DFW Metroplex</span>
              </m.div>

              <h1 className="font-heading text-4xl sm:text-5xl md:text-6xl lg:text-7xl text-white leading-[1.05] mb-5 tracking-tight font-bold drop-shadow-[0_4px_24px_rgba(0,0,0,0.45)]">
                Damage Restored.
                <br />
                Homes <span className="text-gradient-primary">Rebuilt</span>.
              </h1>

              {/* Readable tagline with glass background card */}
              <div className="inline-block bg-black/60 backdrop-blur-md border border-white/15 rounded-2xl px-5 py-4 mb-8 max-w-xl shadow-xl">
                <p className="text-white text-base md:text-lg leading-relaxed">
                  <span className="font-bold text-white">R&amp;R House Renovation LLC</span> are
                  insurance claim specialists and full-service renovation contractors — water, fire
                  and storm damage restoration, roofing, concrete, carpentry, flooring, painting,
                  and complete kitchen and bathroom remodels across DFW.
                </p>
              </div>

              <div className="flex flex-wrap gap-3 mb-8">
                <Button
                  size="lg"
                  onClick={openQuoteForm}
                  className="text-base gap-2 px-7 py-6 rounded-xl font-semibold shadow-xl"
                >
                  Get Free Estimate
                </Button>
                <Button asChild variant="outline" size="lg" className="text-base gap-2 px-7 py-6 rounded-xl border-2 border-white/30 text-white bg-white/10 hover:bg-white/20 backdrop-blur-sm font-semibold">
                  <a href={siteConfig.phoneHref} aria-label={`Call ${siteConfig.name} at ${siteConfig.phone}`}>
                    <Phone className="h-5 w-5" /> {siteConfig.phone}
                  </a>
                </Button>
              </div>

              <m.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.6, duration: 0.5 }}
                className="glass-card rounded-xl px-5 py-3 inline-flex items-center gap-3"
              >
                <span className="text-2xl">🎁</span>
                <span className="text-white font-semibold text-sm">
                  <span className="text-secondary-light font-bold">10% off</span> your next referral project ·{" "}
                  <span className="text-secondary-light font-bold">5% off</span> for referred clients
                </span>
              </m.div>
            </m.div>
          </div>

          {/* Form widget — exactly as currently positioned, right-side hero, original dimensions preserved */}
          <m.div
            initial={{ opacity: 0, x: 40 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.3, duration: 0.7 }}
            className="lg:col-span-5"
          >
            <div className="widget-shell bg-white">
              <div className="px-6 pt-6 pb-2">
                <h2 className="font-heading text-2xl text-foreground font-bold tracking-tight">
                  Get Your Free Estimate
                </h2>
                <p className="text-muted-foreground text-sm mt-1">
                  Damage inspection or remodel quote — no pressure, no obligation.
                </p>
              </div>
              <FormEmbed className="p-4 sm:p-6" />
            </div>
          </m.div>
        </div>

        <m.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.7, duration: 0.5 }}
          className="mt-10 lg:mt-12 glass-card rounded-2xl p-5 md:p-6 gradient-border"
        >
          <div className="flex flex-wrap items-center justify-around gap-5">
            {stats.map((stat, i) => (
              <m.div
                key={stat.label}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.8 + i * 0.15, duration: 0.5 }}
                className="text-center"
              >
                <div className="font-heading text-3xl md:text-4xl text-white tracking-wide font-bold">
                  {stat.value}<span className="text-secondary">{stat.suffix}</span>
                </div>
                <div className="text-white/90 text-xs font-medium uppercase tracking-wider mt-1">{stat.label}</div>
              </m.div>
            ))}
            {badges.map(({ icon: Icon, label }) => (
              <div key={label} className="flex items-center gap-2">
                <div className="h-9 w-9 rounded-lg bg-secondary/25 flex items-center justify-center shrink-0">
                  <Icon className="h-4 w-4 text-secondary" />
                </div>
                <span className="text-white/85 text-xs sm:text-sm font-medium">{label}</span>
              </div>
            ))}
          </div>
        </m.div>
      </div>

      {/* Concave/curved bottom edge */}
      <div className="pointer-events-none absolute -bottom-px left-0 right-0 h-20 md:h-24 leading-none">
        <svg
          viewBox="0 0 1440 96"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
          className="block w-full h-full"
          preserveAspectRatio="none"
        >
          <path
            d="M0 0C240 96 480 96 720 64C960 32 1200 32 1440 64V96H0V0Z"
            fill="hsl(var(--background))"
          />
        </svg>
      </div>
    </section>
  );
};

export default HeroSection;
